import { useSyncExternalStore } from 'react';
import en from '../locales/en.json';

/**
 * Translation runtime.
 *
 * Deliberately ~200 lines against a library, for two reasons. The plural rules, the date, number and
 * unit formatting — the parts that are genuinely hard and genuinely locale-specific — come from
 * `Intl`, which the platform already ships and gets right for every locale CLDR knows. What is left
 * is key lookup and `{placeholder}` interpolation, and that is not worth 40 kB of bundle to a product
 * that spent TASK-0023 getting the admin routes down from 749 kB to 351 kB. See ADR 0027.
 *
 * **Adding a language is adding one JSON file** under `src/locales/` and nothing else — no import, no
 * registration, no TypeScript. The list of available locales is derived from the files themselves at
 * build time, which is the property `docs/run-books/add-a-language.md` promises to contributors and
 * `scripts/ci/i18n-catalogues.mjs` enforces.
 */

/** One entry: a plain string, or the plural forms CLDR defines for the language. */
export type Entry = string | Partial<Record<Intl.LDMLPluralRule | 'zero', string>>;
export type Catalogue = Record<string, Entry>;
export type Params = Record<string, string | number>;

/**
 * English is the fallback for every missing key in every language, so it is imported statically and
 * is always in the bundle. Every other catalogue is fetched on demand.
 */
const EN = en as Catalogue;

/**
 * `import.meta.glob` resolves at build time, so the set of languages is a fact about the directory
 * rather than about this file. Lazy, so a Spanish session does not download French.
 */
const LOADERS = import.meta.glob<{ default: Catalogue }>('../locales/*.json');

const tagOf = (path: string) => path.slice(path.lastIndexOf('/') + 1, -'.json'.length);

/** Every locale with a catalogue, sorted, English first. */
export const AVAILABLE: readonly string[] = Object.keys(LOADERS)
  .map(tagOf)
  .sort((a, b) => (a === 'en' ? -1 : b === 'en' ? 1 : a.localeCompare(b)));

const primary = (tag: string) => tag.toLowerCase().split('-')[0] ?? '';

/**
 * Best available match for a list of requested tags, in the caller's order of preference. Exact match
 * wins, then the primary subtag — so a browser asking for `es-419` gets `es`, and one asking for `es`
 * gets `es-419` if that is what we ship. Returns null when nothing matches, so callers can fall
 * through to their own default rather than being handed English by surprise.
 */
export function matchLocale(requested: readonly string[], available: readonly string[]): string | null {
  for (const want of requested) {
    const exact = available.find((a) => a.toLowerCase() === want.toLowerCase());
    if (exact) return exact;
    const byPrimary = available.find((a) => primary(a) === primary(want));
    if (byPrimary) return byPrimary;
  }
  return null;
}

/**
 * The locale to render in. `preference` is the user's stored choice — `'auto'` (or anything we do not
 * have) defers to what the browser asked for, which is `navigator.languages`, itself derived from the
 * same list the browser sends as `Accept-Language`.
 */
export function resolveLocale(
  preference: string,
  accepted: readonly string[],
  available: readonly string[] = AVAILABLE,
): string {
  if (preference && preference !== 'auto') {
    const chosen = matchLocale([preference], available);
    if (chosen) return chosen;
  }
  return matchLocale(accepted, available) ?? 'en';
}

/**
 * A language's name **in that language** — `English`, `español`. From `Intl.DisplayNames`, so a
 * contributor who adds a catalogue gets a correctly capitalised, correctly accented entry in the
 * language menu without writing one; the tag itself is the fallback if the platform has no name.
 */
export function localeName(tag: string): string {
  try {
    return new Intl.DisplayNames([tag], { type: 'language' }).of(tag) ?? tag;
  } catch {
    return tag;
  }
}

const KEY = 'mailserver.locale';

interface State {
  /** The user's stored choice: `'auto'` or a tag. */
  preference: string;
  /** What `preference` resolved to against the catalogues we actually have. */
  locale: string;
  catalogue: Catalogue;
}

const listeners = new Set<() => void>();
const accepted = (): readonly string[] => globalThis.navigator?.languages ?? [];

function loadPreference(): string {
  try {
    return globalThis.localStorage?.getItem(KEY) ?? 'auto';
  } catch {
    return 'auto'; // private mode, or storage disabled
  }
}

let state: State = { preference: 'auto', locale: 'en', catalogue: EN };

/** Notifies subscribers. `state` is replaced wholesale so `useSyncExternalStore` sees a new object. */
function commit(next: State): void {
  state = next;
  applyLocale();
  listeners.forEach((l) => l());
}

/**
 * Writes the resolved locale onto `<html>`. Screen readers switch voice and pronunciation on `lang`,
 * and hyphenation follows it, so this is not decoration — an untranslated `lang="en"` on Spanish text
 * is read aloud in an English accent.
 */
export function applyLocale(doc: Document | undefined = globalThis.document): void {
  if (!doc) return;
  doc.documentElement.lang = state.locale;
  doc.documentElement.dir = 'ltr'; // every catalogue we ship is LTR; RTL flips this and nothing else
}

const cache = new Map<string, Catalogue>([['en', EN]]);

async function catalogueFor(locale: string): Promise<Catalogue> {
  const hit = cache.get(locale);
  if (hit) return hit;
  const load = LOADERS[`../locales/${locale}.json`];
  if (!load) return EN;
  try {
    const mod = await load();
    cache.set(locale, mod.default);
    return mod.default;
  } catch {
    return EN; // a catalogue that will not load is a missing translation, not a broken app
  }
}

/**
 * Applies a preference and loads its catalogue.
 *
 * The locale switches as soon as it is known, which is before the catalogue can arrive. **The
 * catalogue is replaced in that same step**, with the cached one if there is one and English
 * otherwise — carrying the *previous* language's over would render Spanish while French loads, which
 * is worse than falling back, because it looks like the language did not change rather than like a
 * translation is missing. A language already in the cache switches in one step with no window at
 * all, which is every switch back to English.
 */
export async function setLocale(preference: string): Promise<void> {
  const locale = resolveLocale(preference, accepted());
  try {
    localStorage.setItem(KEY, preference);
  } catch {
    /* the choice just will not survive this browser */
  }
  const cached = cache.get(locale);
  commit({ preference, locale, catalogue: cached ?? EN });
  if (cached) return;
  const catalogue = await catalogueFor(locale);
  if (state.locale === locale) commit({ ...state, catalogue });
}

export const getLocale = (): string => state.locale;
export const getPreference = (): string => state.preference;
const snapshot = (): State => state;

export function useLocale(): State {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    snapshot,
    snapshot,
  );
}

/** Reads the stored preference and loads its catalogue. Called once, from `main.tsx`. */
export async function initI18n(): Promise<void> {
  await setLocale(loadPreference());
}

/**
 * Picks the plural form. `zero` is not a CLDR category for English or Spanish, so it is offered as an
 * explicit override on top: a catalogue that spells out "No messages" gets it, one that does not
 * falls through to the ordinary rules and renders "0 messages".
 */
function plural(entry: Exclude<Entry, string>, count: number, locale: string): string {
  if (count === 0 && entry.zero !== undefined) return entry.zero;
  const category = new Intl.PluralRules(locale).select(count);
  return entry[category] ?? entry.other ?? '';
}

const PLACEHOLDER = /\{(\w+)\}/g;

/**
 * Looks a key up in the active catalogue, then English, then gives back the key itself. A missing key
 * therefore renders the English string in a translated UI and only ever degrades to a key name when
 * the key exists in no catalogue at all — which `pnpm i18n:check` fails the build on, so it should
 * not reach anyone.
 */
export function translate(key: string, params?: Params, at: State = state): string {
  const entry = at.catalogue[key] ?? EN[key];
  if (entry === undefined) {
    if (import.meta.env.DEV) console.warn(`[i18n] no string for "${key}"`);
    return key;
  }
  const raw =
    typeof entry === 'string'
      ? entry
      : plural(entry, typeof params?.['count'] === 'number' ? params['count'] : 0, at.locale);
  if (!params) return raw;
  return raw.replace(PLACEHOLDER, (whole, name: string) => {
    const v = params[name];
    if (v === undefined) return whole;
    return typeof v === 'number' ? new Intl.NumberFormat(at.locale).format(v) : v;
  });
}

export type TFunction = (key: string, params?: Params) => string;

/** The translator for the active locale. Re-renders every consumer when the locale changes. */
export function useT(): TFunction {
  const at = useLocale();
  return (key, params) => translate(key, params, at);
}
